
import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

const LakesidePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/06986d0c2c3f7eb4e0c4fa0813ff2ff4.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">Lakeside Serenity</h1>
          <p className="text-xl md:text-2xl mb-8 leading-relaxed">
            Find peace and tranquility at our pristine lakeside retreats, where crystal-clear waters meet luxury accommodations
          </p>
          <Link 
            to="/book" 
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-block"
          >
            Book Lakeside Experience
          </Link>
        </div>
      </section>

      {/* Experience Overview */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Waterfront Luxury</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our lakeside retreats offer the perfect blend of tranquility and adventure. Wake up to stunning water views, 
                enjoy the gentle lapping of waves, and experience the therapeutic benefits of being near pristine natural waters.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Each lakeside location features premium waterfront accommodations with private docks, panoramic lake views, 
                and direct access to a variety of water activities and peaceful shoreline experiences.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">25+</div>
                  <div className="text-gray-600">Pristine Lakes</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">18</div>
                  <div className="text-gray-600">Lakeside Locations</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Luxury%20lakeside%20glamping%20tent%20with%20panoramic%20water%20views%2C%20modern%20eco-friendly%20accommodation%20on%20wooden%20platform%20over%20water%2C%20private%20deck%20with%20outdoor%20furniture%2C%20crystal%20clear%20lake%20surrounded%20by%20mountains%2C%20premium%20waterfront%20design&width=600&height=400&seq=lakeside-glamp&orientation=landscape"
                alt="Lakeside Glamping"
                className="rounded-lg shadow-xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section className="py-20 bg-blue-50 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Lakeside Activities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From peaceful water meditation to exciting aquatic adventures, our lakes offer something for everyone
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-sailboat-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Water Sports</h3>
              <p className="text-gray-700 leading-relaxed">
                Enjoy kayaking, paddleboarding, and sailing on pristine waters with professional instruction and premium equipment.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-fish-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Fishing Expeditions</h3>
              <p className="text-gray-700 leading-relaxed">
                Experience world-class fishing with guided expeditions for trout, bass, and other native species in pristine waters.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-sun-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Sunrise Yoga</h3>
              <p className="text-gray-700 leading-relaxed">
                Start your day with peaceful yoga sessions on floating platforms, surrounded by the tranquil sounds of nature.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Locations */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Lakeside Locations</h2>
            <p className="text-xl text-gray-600">Discover our most stunning waterfront retreat destinations</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Alpine%20lake%20surrounded%20by%20snow-capped%20mountains%2C%20luxury%20lakeside%20resort%20with%20wooden%20cabins%2C%20crystal%20clear%20turquoise%20water%2C%20pristine%20wilderness%20setting%2C%20premium%20eco-accommodation%20with%20panoramic%20mountain%20and%20lake%20views&width=600&height=300&seq=alpine-lake&orientation=landscape"
                alt="Alpine Lake Retreat"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Alpine Lake Paradise</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Experience the pristine beauty of high-altitude alpine lakes surrounded by majestic mountain peaks and untouched wilderness.
                </p>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <i className="ri-map-pin-line mr-2"></i>Montana, USA
                  </div>
                  <a 
                    href="/retreats" 
                    className="text-blue-600 hover:text-blue-700 font-semibold cursor-pointer whitespace-nowrap"
                  >
                    Learn More <i className="ri-arrow-right-line ml-1"></i>
                  </a>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Peaceful%20forest%20lake%20with%20luxury%20floating%20cabins%2C%20calm%20reflective%20water%20surrounded%20by%20dense%20pine%20forest%2C%20wooden%20boardwalks%20and%20docks%2C%20serene%20natural%20setting%20perfect%20for%20relaxation%20and%20water%20activities%2C%20premium%20lakeside%20accommodation&width=600&height=300&seq=forest-lake&orientation=landscape"
                alt="Forest Lake Retreat"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Forest Lake Haven</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Immerse yourself in the tranquility of secluded forest lakes, where ancient trees meet crystal-clear waters.
                </p>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <i className="ri-map-pin-line mr-2"></i>Minnesota, USA
                  </div>
                  <a 
                    href="/retreats" 
                    className="text-blue-600 hover:text-blue-700 font-semibold cursor-pointer whitespace-nowrap"
                  >
                    Learn More <i className="ri-arrow-right-line ml-1"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-blue-600 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Dive Into Lakeside Luxury</h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Book your lakeside retreat today and experience the perfect harmony of water, nature, and luxury
          </p>
          <Link 
            to="/book" 
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-full text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap inline-block"
          >
            Book Your Lakeside Retreat
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LakesidePage;
